import { useState, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ChevronsUpDownIcon, XIcon } from 'lucide-react';
import { Badge } from '@ui/badge.tsx';
import { Button } from '@ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { useApiAdminsegmentsGetCollection } from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useDebounce } from '@r/hooks/use-debounce.ts';

export interface PickedSegment {
  id: number;
  name: string;
}

interface SegmentPickerProps {
  selectedSegments: PickedSegment[];
  onSelect: (segment: PickedSegment) => void;
  onRemove: (segment: PickedSegment) => void;
}

/**
 * Sélecteur de segments contrôlé : recherche/ajout via une combobox et retrait via les badges.
 * Ne fait aucun appel d'écriture, c'est au parent de persister (immédiatement ou après création).
 */
export const SegmentPicker: FC<SegmentPickerProps> = ({ selectedSegments, onSelect, onRemove }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 300);

  const allSegmentsQuery = useApiAdminsegmentsGetCollection({
    perPage: 100,
    search: debouncedSearch || undefined
  });

  const selectedSegmentIds = new Set(selectedSegments.map((segment) => segment.id));
  const allSegments = (allSegmentsQuery.data?.data ?? []) as SegmentSegmentCollectionReadModel[];
  const availableSegments = allSegments.filter((segment) => !selectedSegmentIds.has(segment.id));

  const handleSelect = (segment: SegmentSegmentCollectionReadModel) => {
    setIsOpen(false);
    onSelect({ id: segment.id, name: segment.name });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('admin.segments.conditioning.select.label')}</p>

        <Popover
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setSearchValue('');
          }}
        >
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="w-full justify-between font-normal">
              {t('admin.segments.conditioning.select.placeholder')}
              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={t('common.search')}
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList>
                <CommandEmpty>{t('common.no.results.found')}</CommandEmpty>
                <CommandGroup>
                  {availableSegments.map((segment) => (
                    <CommandItem key={segment.id} onSelect={() => handleSelect(segment)}>
                      {segment.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selectedSegments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedSegments.map((segment) => (
            <Badge key={segment.id} variant="secondary" className="gap-1 px-3 py-1">
              {segment.name}
              <button
                type="button"
                onClick={() => onRemove(segment)}
                aria-label={t('admin.segments.conditioning.remove')}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-end justify-between gap-4">
        <p className="text-xs text-muted-foreground/80">
          <Trans
            i18nKey="admin.segments.conditioning.default.description"
            components={{ 1: <br /> }}
          />
        </p>

        <Link
          to="/settings/segments"
          className="whitespace-nowrap text-sm font-medium underline underline-offset-2"
        >
          {t('admin.segments.conditioning.manage.link')}
        </Link>
      </div>
    </div>
  );
};
